import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../components/common/SafeIcon';
import FanProfileEditor from '../../components/fan/FanProfileEditor';
import Navbar from '../../components/nav/Navbar';
import { useAuth } from '../../context/AuthContext';
import { useFanProfileData } from '../../hooks/useFanProfileData';
import { useFanFollows } from '../../hooks/useFollows';

const {
  FiBox, FiHeart, FiPlayCircle, FiDownload, FiArrowLeft,
  FiMapPin, FiLink, FiInstagram, FiTwitter, FiUser, FiEdit2,
  FiCamera, FiMusic, FiLoader, FiZap, FiMail, FiCheck,
} = FiIcons;

const StatPill = ({ label, value, loading }) => (
  <div className="glass-card rounded-2xl px-5 py-3 text-center min-w-[90px]">
    {loading
      ? <div className="h-6 w-10 mx-auto bg-white/5 rounded animate-pulse" />
      : <p className="text-xl font-black text-white">{value ?? 0}</p>
    }
    <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mt-0.5">{label}</p>
  </div>
);

const EmptyTab = ({ icon, title, sub, action }) => (
  <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
    <div className="w-16 h-16 rounded-3xl glass-card flex items-center justify-center">
      <SafeIcon icon={icon} className="text-2xl text-zinc-600" />
    </div>
    <p className="font-black text-lg uppercase tracking-tight text-zinc-500">{title}</p>
    <p className="text-xs font-bold text-zinc-700 max-w-xs">{sub}</p>
    {action}
  </div>
);

const FanProfile = () => {
  const navigate = useNavigate();
  const { user, profile, refetchProfile } = useAuth();

  const { unlockedDrops, subscriptions, stats, loading: dataLoading } = useFanProfileData(user?.id);
  const { follows, loading: followsLoading } = useFanFollows(user?.id);

  const [activeTab,  setActiveTab]  = useState('activity');
  const [showEditor, setShowEditor] = useState(false);

  const displayName = profile?.display_name || user?.email?.split('@')[0] || 'Fan';
  const genres      = profile?.favorite_genres || [];

  const handleSaved = () => { refetchProfile?.(); setShowEditor(false); };

  const tabs = [
    { id: 'activity',  label: 'Activity',  icon: FiZap   },
    { id: 'library',   label: 'Library',  icon: FiBox   },
    { id: 'following', label: 'Following', icon: FiHeart },
  ];

  const followCount = follows.length;

  // Combine unlocks and follows into a single chronological feed
  const activityFeed = [
    ...unlockedDrops.map(u => ({ ...u, type: 'unlock', date: new Date(u.created_at).getTime() })),
    ...follows.map(f => ({ ...f, type: 'follow', date: new Date(f.created_at).getTime() }))
  ].sort((a, b) => b.date - a.date);

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <Navbar />

      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="ambient-orb-pink  w-[500px] h-[500px] top-[-10%] right-[-10%] animate-caustic" />
        <div className="ambient-orb-coral w-[400px] h-[400px] bottom-[-10%] left-[-10%] animate-caustic"
          style={{ animationDelay: '2s' }} />
      </div>

      {/* Header */}
      <header className="relative pt-32 pb-12 px-6 md:px-12 border-b border-white/5">
        <div className="max-w-5xl mx-auto space-y-8">

          <div className="flex items-center justify-between">
            <button onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors group">
              <div className="w-8 h-8 rounded-xl glass-card flex items-center justify-center group-hover:border-brand-pink/30 transition-all">
                <SafeIcon icon={FiArrowLeft} className="text-sm" />
              </div>
              <span className="text-xs font-black uppercase tracking-widest">Back</span>
            </button>

            {/* Newsletter shortcut */}
            <Link to="/newsletter"
              className="flex items-center gap-2 px-4 py-2.5 glass-card rounded-2xl text-xs font-black uppercase tracking-widest text-zinc-400 hover:text-brand-pink hover:border-brand-pink/30 transition-all">
              <SafeIcon icon={FiMail} className="text-sm" /> Digest Settings
            </Link>
          </div>

          <div className="flex flex-col md:flex-row items-start md:items-end gap-8">
            {/* Avatar */}
            <div className="relative group flex-shrink-0">
              <div className="w-32 h-32 rounded-3xl overflow-hidden border-2 border-brand-pink/30 shadow-[0_0_30px_rgba(255,102,204,0.15)]">
                {profile?.avatar_url ? (
                  <img src={profile.avatar_url} alt={displayName} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-brand-pink/20 to-brand-coral/10 flex items-center justify-center">
                    <SafeIcon icon={FiUser} className="text-4xl text-brand-pink/50" />
                  </div>
                )}
              </div>
              {user && (
                <button onClick={() => setShowEditor(true)}
                  className="absolute inset-0 rounded-3xl bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <SafeIcon icon={FiCamera} className="text-white text-xl" />
                </button>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 space-y-3">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="badge-glass text-brand-pink border-brand-pink/20">Fan</span>
                {genres.slice(0, 3).map(g => (
                  <span key={g} className="badge-glass">{g}</span>
                ))}
              </div>
              <h1 className="text-5xl md:text-6xl font-black tracking-tighter uppercase text-white leading-none">
                {displayName}
              </h1>
              {profile?.username && (
                <p className="text-zinc-500 font-black tracking-widest text-sm uppercase">@{profile.username}</p>
              )}
              {profile?.bio && (
                <p className="text-zinc-400 font-bold text-sm max-w-xl leading-relaxed">{profile.bio}</p>
              )}
              <div className="flex flex-wrap items-center gap-4 text-xs font-bold text-zinc-600">
                {profile?.location && (
                  <span className="flex items-center gap-1.5">
                    <SafeIcon icon={FiMapPin} className="text-xs text-brand-pink" /> {profile.location}
                  </span>
                )}
                {profile?.website && (
                  <a href={profile.website} target="_blank" rel="noreferrer"
                    className="flex items-center gap-1.5 hover:text-brand-pink transition-colors">
                    <SafeIcon icon={FiLink} className="text-xs" /> Website
                  </a>
                )}
                {profile?.instagram && (
                  <a href={`https://instagram.com/${profile.instagram}`} target="_blank" rel="noreferrer"
                    className="flex items-center gap-1.5 hover:text-brand-pink transition-colors">
                    <SafeIcon icon={FiInstagram} className="text-xs" /> @{profile.instagram}
                  </a>
                )}
                {profile?.twitter && (
                  <a href={`https://twitter.com/${profile.twitter}`} target="_blank" rel="noreferrer"
                    className="flex items-center gap-1.5 hover:text-brand-pink transition-colors">
                    <SafeIcon icon={FiTwitter} className="text-xs" /> @{profile.twitter}
                  </a>
                )}
              </div>
            </div>

            {user && (
              <button onClick={() => setShowEditor(true)}
                className="flex items-center gap-2 px-6 py-3 btn-brand rounded-2xl text-xs flex-shrink-0">
                <SafeIcon icon={FiEdit2} /> Edit Profile
              </button>
            )}
          </div>

          {/* Stats */}
          <div className="flex gap-3 flex-wrap">
            <StatPill label="Library"   value={stats?.library}   loading={dataLoading} />
            <StatPill label="Following" value={followCount}       loading={followsLoading} />
            <StatPill label="Unlocked"  value={stats?.unlocked}  loading={dataLoading} />
          </div>
        </div>
      </header>

      {/* Tab nav */}
      <nav className="sticky top-0 z-40 bg-zinc-950/85 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6 md:px-12 flex gap-8">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`relative py-6 flex items-center gap-2 text-[11px] font-black uppercase tracking-widest transition-all ${
                activeTab === t.id ? 'text-white' : 'text-zinc-600 hover:text-zinc-300'
              }`}>
              <SafeIcon icon={t.icon} className="text-base" />
              {t.label}
              {t.id === 'following' && followCount > 0 && (
                <span className="px-1.5 py-0.5 bg-brand-pink/20 text-brand-pink text-[9px] rounded-full font-black">
                  {followCount}
                </span>
              )}
              {activeTab === t.id && (
                <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-brand-pink to-brand-coral rounded-t-full" />
              )}
            </button>
          ))}
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-5xl mx-auto py-12 px-6 md:px-12 relative z-10">

        {/* Activity tab */}
        {activeTab === 'activity' && (
          (dataLoading || followsLoading) ? (
            <div className="flex items-center justify-center py-24">
              <SafeIcon icon={FiLoader} className="text-3xl text-brand-pink animate-spin" />
            </div>
          ) : activityFeed.length === 0 ? (
            <EmptyTab icon={FiZap} title="No Activity Yet"
              sub="Unlock drops or follow artists to build your activity feed." />
          ) : (
            <div className="space-y-4">
              {activityFeed.map(item => (
                <div key={`${item.type}-${item.id}`} className="glass-card rounded-3xl p-5 flex items-center justify-between hover:border-brand-pink/20 transition-all group">
                  {item.type === 'unlock' ? (
                    <div className="flex items-center gap-5">
                      <div className="w-16 h-16 rounded-2xl overflow-hidden border border-white/10 bg-zinc-900 flex-shrink-0">
                        {item.drop?.cover_url ? (
                          <img src={item.drop.cover_url} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" alt={item.drop.title} />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <SafeIcon icon={FiMusic} className="text-2xl text-zinc-600" />
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-zinc-400 font-bold mb-0.5">Unlocked a drop</p>
                        <div className="flex items-center gap-2">
                          <h4 className="font-black text-base uppercase tracking-tight text-white">
                            {item.drop?.title || 'Untitled Drop'}
                          </h4>
                        </div>
                        {item.amount_paid > 0 && (
                          <p className="text-[10px] text-brand-pink font-bold mt-0.5">
                            Paid ${item.amount_paid}
                          </p>
                        )}
                        <p className="text-[10px] text-zinc-700 font-bold mt-1">
                          {new Date(item.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-5">
                      <div className="w-16 h-16 rounded-2xl overflow-hidden border border-white/10 bg-zinc-900 flex-shrink-0">
                        {item.artist?.avatar_image ? (
                          <img src={item.artist.avatar_image} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" alt={item.artist.name} />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <SafeIcon icon={FiUser} className="text-2xl text-zinc-600" />
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-zinc-400 font-bold mb-0.5">Started following</p>
                        <div className="flex items-center gap-2">
                          <h4 className="font-black text-base uppercase tracking-tight text-white">
                            {item.artist?.name || 'Unknown Artist'}
                          </h4>
                          {item.artist?.verified && <SafeIcon icon={FiCheck} className="text-blue-400 text-xs" />}
                        </div>
                        {item.artist?.genre && (
                          <p className="text-[10px] font-black tracking-widest uppercase text-zinc-500 mt-0.5">
                            {item.artist.genre}
                          </p>
                        )}
                        <p className="text-[10px] text-zinc-700 font-bold mt-1">
                          {new Date(item.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex-shrink-0">
                    {item.type === 'unlock' ? (
                      <span className="w-10 h-10 rounded-full glass-card flex items-center justify-center text-brand-pink border border-brand-pink/20">
                        <SafeIcon icon={FiBox} />
                      </span>
                    ) : (
                      <Link to={`/${item.artist?.slug}`} className="w-10 h-10 rounded-full glass-card flex items-center justify-center text-brand-pink border border-brand-pink/20 hover:bg-brand-pink/10 transition-colors">
                        <SafeIcon icon={FiHeart} />
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {/* Library tab */}
        {activeTab === 'library' && (
          dataLoading ? (
            <div className="flex items-center justify-center py-24">
              <SafeIcon icon={FiLoader} className="text-3xl text-brand-pink animate-spin" />
            </div>
          ) : unlockedDrops.length === 0 ? (
            <EmptyTab icon={FiBox} title="Library is Empty"
              sub="Unlock exclusive drops from your favourite artists and they'll appear here." />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {unlockedDrops.map(({ id, drop, amount_paid, created_at }) => (
                <div key={id} className="group glass-card rounded-3xl overflow-hidden hover:-translate-y-1 transition-all duration-300">
                  <div className="aspect-square relative overflow-hidden bg-zinc-900">
                    {drop?.cover_url
                      ? <img src={drop.cover_url}
                          className="w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                          alt={drop.title} />
                      : <div className="w-full h-full flex items-center justify-center">
                          <SafeIcon icon={FiMusic} className="text-5xl text-zinc-700" />
                        </div>
                    }
                    {drop?.genre && (
                      <div className="absolute top-4 left-4">
                        <span className="badge-glass">{drop.genre}</span>
                      </div>
                    )}
                    {amount_paid > 0 && (
                      <div className="absolute top-4 right-4">
                        <span className="badge-glass text-brand-pink border-brand-pink/20">
                          <SafeIcon icon={FiZap} className="text-[9px] mr-1" />${amount_paid}
                        </span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center gap-4">
                      <button className="w-14 h-14 bg-white text-black rounded-full flex items-center justify-center hover:scale-110 shadow-2xl transition-transform">
                        <SafeIcon icon={FiPlayCircle} className="text-2xl" />
                      </button>
                      <button className="w-14 h-14 glass-strong text-white rounded-full flex items-center justify-center hover:scale-110 shadow-2xl transition-transform">
                        <SafeIcon icon={FiDownload} className="text-xl" />
                      </button>
                    </div>
                  </div>
                  <div className="p-6 space-y-1">
                    <h3 className="font-black text-base uppercase tracking-tight text-white truncate">
                      {drop?.title || 'Untitled Drop'}
                    </h3>
                    <p className="text-zinc-600 text-[10px] font-black tracking-widest uppercase">
                      Unlocked {new Date(created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {/* Following tab */}
        {activeTab === 'following' && (
          followsLoading ? (
            <div className="flex items-center justify-center py-24">
              <SafeIcon icon={FiLoader} className="text-3xl text-brand-pink animate-spin" />
            </div>
          ) : follows.length === 0 ? (
            <EmptyTab
              icon={FiHeart}
              title="Not Following Anyone"
              sub="Follow artists on their pages to get their drops in your weekly digest."
              action={
                <Link to="/discover"
                  className="px-6 py-3 btn-brand rounded-2xl text-xs">
                  Discover Artists
                </Link>
              }
            />
          ) : (
            <div className="space-y-4">
              {/* Newsletter CTA */}
              <div className="p-5 bg-brand-pink/5 border border-brand-pink/20 rounded-2xl flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiMail} className="text-brand-pink text-lg flex-shrink-0" />
                  <div>
                    <p className="text-xs font-black uppercase tracking-widest text-white">Weekly Digest</p>
                    <p className="text-[10px] text-zinc-500 font-bold">Get drops from these {followCount} artists in your inbox</p>
                  </div>
                </div>
                <Link to="/newsletter"
                  className="flex-shrink-0 px-4 py-2.5 border border-brand-pink/40 text-brand-pink rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-brand-pink/10 transition-all">
                  Set Up →
                </Link>
              </div>

              {follows.map(({ id, artist, created_at }) => (
                <Link
                  key={id}
                  to={`/${artist?.slug}`}
                  className="glass-card rounded-3xl p-5 flex items-center justify-between hover:border-brand-pink/20 transition-all group cursor-pointer"
                >
                  <div className="flex items-center gap-5">
                    <div className="w-16 h-16 rounded-2xl overflow-hidden border border-white/10 bg-zinc-900 flex-shrink-0">
                      {artist?.avatar_image
                        ? <img src={artist.avatar_image}
                            className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
                            alt={artist.name} />
                        : <div className="w-full h-full flex items-center justify-center">
                            <SafeIcon icon={FiUser} className="text-2xl text-zinc-600" />
                          </div>
                      }
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-black text-base uppercase tracking-tight text-white">
                          {artist?.name || 'Unknown Artist'}
                        </h4>
                        {artist?.verified && (
                          <SafeIcon icon={FiCheck} className="text-blue-400 text-xs" />
                        )}
                      </div>
                      {artist?.genre && (
                        <p className="text-[10px] font-black tracking-widest uppercase text-zinc-500 mt-0.5">
                          {artist.genre}
                        </p>
                      )}
                      <p className="text-[10px] text-zinc-700 font-bold mt-0.5">
                        Following since {new Date(created_at).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                      </p>
                    </div>
                  </div>
                  <span className="px-4 py-2 glass-card rounded-xl text-[10px] font-black uppercase tracking-widest text-brand-pink border-brand-pink/20">
                    Following
                  </span>
                </Link>
              ))}
            </div>
          )
        )}
      </main>

      {/* Edit Profile Modal */}
      {showEditor && (
        <FanProfileEditor
          profile={profile}
          userId={user?.id}
          onClose={() => setShowEditor(false)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

export default FanProfile;